/**
 * MypageSubscribeContainer
 * --
 */
import React from 'react';
import MypageSubscribePresenter from './MypageSubscribePresenter';
import { useUserDataAction, useUserDataStack, useUserStack } from 'store';
import {
  useBulkUpdateParcelService,
  useConfirmParcelServiceStatus,
} from 'hooks/ParcelServiceHooks';
import MessageAlert, {
  POINT_STORE_ID,
  openPopup,
  DOMAIN_URL,
  DEPLOY_TYPE,
} from 'utils';
import { useCreateRefundSubscription } from 'hooks/RefundHooks';
import {
  usePaymentCancelCard,
  usePaymentDeRegistBilling,
} from 'hooks/PaymentHooks.';
import { useMyShopOrderList } from 'hooks/custom';
import { useUpdateCard } from 'hooks/CardHooks';

const TAP = [
  {
    label: '신청내역',
    value: 'ACTIVE',
  },
  {
    label: '해지내역',
    value: 'INACTIVE',
  },
];

const MypageSubscribeContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { navigate, screenSize } = props;

  /* ===== Mutate ===== */
  const { confirmParcelServiceStatus } = useConfirmParcelServiceStatus();
  const { createRefundSubscription } = useCreateRefundSubscription();
  const { cancelCard } = usePaymentCancelCard();

  const { deregistBilling } = usePaymentDeRegistBilling();
  const { bulkUpdateParcelService } = useBulkUpdateParcelService();
  const { updateCard } = useUpdateCard();

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const { reviews, cardList } = useUserDataStack();
  const userDataAction = useUserDataAction();

  /* ===== Hooks ===== */
  const orderList = useMyShopOrderList(userInfo.user_id);

  /* ===== STATE ===== */
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // Tap
  const [selectedTab, setSelectedTab] = React.useState('ACTIVE');

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 구매확정, 취소/환불
   * --
   * @param {string} type
   * @param {object} data
   * @returns
   */
  const handleOrderStatus = async (type, data) => {
    const submitType = type.toLocaleUpperCase();

    try {
      const confirm = (msg) => {
        return window.confirm(msg);
      };

      switch (submitType) {
        // SECTION: 정기배송 구매확정
        case 'SUBSCRIPTION-CONFIRMED':
          if (
            !confirm(
              '상품 주문을 확정하시겠습니까?\n주문 확정 후 취소/환불은 불가능합니다.',
            )
          )
            return;

          /**
           * STUB data 형식
           * --
           *  {
           *    shop_order_id: number
           *
           *    parcel_service_id: number
           *    purchase_status: string
           *    regular_payment_id: number
           *    regular_payment_info: object
           *  }
           */
          const newConfirmData = data.regular_payment_info.map((info) =>
            info.regular_payment_id !== data.regular_payment_id
              ? info
              : {
                  ...info,
                  purchase_status: data.purchase_status,
                },
          );
          const { status: regularConfirmedStatus } =
            await confirmParcelServiceStatus({
              parcel_service_id: data.parcel_service_id,
              regular_payment_id: data.regular_payment_id,
            });

          if (regularConfirmedStatus !== 200) {
            MessageAlert.warning('주문 확정에 실패하였습니다!');
            return;
          }

          const newRegularOrderList = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          regular_payment_info: newConfirmData,
                        },
                  ),
                },
          );
          userDataAction.setData('orderList', newRegularOrderList);

          MessageAlert.success('상품 주문이 확정되었습니다.');
          return;
        // SECTION: 정기배송 주문취소
        // SECTION: 정기배송 환불
        case 'SUBSCRIPTION-CANCELLED':
        case 'SUBSCRIPTION-REFUNDED':
          if (!data.refund_reason)
            return MessageAlert.info('환불/취소 사유를 선택해주세요');

          /**
           * STUB data 형식
           * --
           *  {
           *    //NOTE - 결제 취소
           *    shop_order_id: number
           *    regular_payment_info: object
           *
           *    //NOTE - 환불 데이터
           *    parcel_service_id: number
           *    refund_reason: string
           *    refund_amount: number
           *    return_num: number
           *    tax_rate: number
           *  }
           */
          if (!confirm('상품 주문을 취소하시겠습니까?')) return;

          setIsLoading(true);

          let regularCancelRes = null;
          // 정기배송이 결제되었을 경우 결제 취소
          if (data.regular_payment_info.payment_info) {
            const regularCancel = {
              mid: POINT_STORE_ID,
              cancelType: 'FULL',
              orgTradeKeyType: 'TID',
              orgTradeKey: data.regular_payment_info.payment_info?.tid,
            };

            regularCancelRes = await cancelCard(regularCancel);

            // NOTE: 결제 취소 실패 시
            if (
              regularCancelRes.status !== 200 ||
              regularCancelRes.data.code !== 'A0200'
            ) {
              MessageAlert.warning('주문 취소에 실패하였습니다!');
              return;
            }
          }

          const cancelRegular = await createRefundSubscription({
            parcel_service_id: data.parcel_service_id,
            refund_reason: data.refund_reason,
            refund_amount: data.regular_payment_info.price,
            refund_num: data.regular_payment_info.num,
            refund_payment_info: regularCancelRes.data.data,
            regular_payment_id: data.regular_payment_info.regular_payment_id,
          });

          if (cancelRegular.status !== 200) {
            MessageAlert.warning('주문 취소에 실패하였습니다!');
            return;
          }

          const newParcelCancelData = orderList.map((order) =>
            order.shop_order_id !== data.shop_order_id
              ? order
              : {
                  ...order,
                  payment_state: 'PARTIALLY_REFUNDED',
                  parcel_service: order.parcel_service.map((parcel) =>
                    parcel.parcel_service_id !== data.parcel_service_id
                      ? parcel
                      : {
                          ...parcel,
                          purchase_status: 'REFUNDED',
                          regular_payment_info: parcel.regular_payment_info.map(
                            (regular) =>
                              regular.regular_payment_id !==
                              data.regular_payment_info.regular_payment_id
                                ? regular
                                : {
                                    ...regular,
                                    payment_state: 'REFUNDED',
                                    purchase_status: 'REFUNDED',
                                  },
                          ),
                          refund: cancelRegular.data,
                        },
                  ),
                },
          );

          userDataAction.setData('orderList', newParcelCancelData);

          MessageAlert.success('상품 주문이 취소되었습니다!');
          return cancelRegular.status;
        default:
          return MessageAlert.info('주문 상태 변경 실패!');
      }
    } catch (err) {
      console.error('[handleOrderStatus] Error!! : ', err);
      MessageAlert.error(
        '상품 취소/환불에 실패하였습니다! 관리자에게 문의해주세요!',
      );
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /**
   * 카드 비활성화 및 정기결제 취소
   * --
   * @param {*} card
   * @returns
   */
  const handleDeleteCard = async (card) => {
    const confirm = (msg = '') => {
      return window.confirm(msg);
    };

    try {
      const { subscribe } = card;

      if (subscribe.length) {
        const check = confirm(
          `카드 삭제 시 현재 진행중인 ${subscribe.length}건의 결제가 취소됩니다.\n정말 삭제하시겠습니까?`,
        );

        if (!check) return;
      } else {
        const check = confirm('카드 정보를 정말 삭제하시겠습니까?');

        if (!check) return;
      }

      // 카드 해지
      const { status: cardStatus } = await updateCard({
        card_id: card.card_id,
        is_enable: 'N',
      });

      if (cardStatus !== 200) {
        MessageAlert.warning('카드 해지에 실패하였습니다!');
        return;
      }

      if (subscribe.length) {
        // 정기배송 구독 해지
        const subData = subscribe.map((sub) => ({
          parcel_service_id: sub.parcel_service_id,
          is_active: 'N',
        }));
        const { status: parcelStatus } = await bulkUpdateParcelService(subData);

        // 정기배송 구독 해지 실패 처리
        if (parcelStatus !== 200) {
          MessageAlert.warning('정기 구독 해지에 실패하였습니다!');
          return;
        }

        const shopId = subscribe[0].shop_order_id;
        // 정기배송 스토어 업데이트
        const newOrderData = orderList.map((order) =>
          order.shop_order_id !== shopId
            ? order
            : {
                ...order,
                parcel_service: order.parcel_service.map((parcel) => ({
                  ...parcel,
                  is_active: 'N',
                })),
              },
        );
        useUserDataAction.setData('orderList', newOrderData);
      }

      // 빌링 토큰 삭제
      const { status: billingStatus, data: billingData } =
        await deregistBilling({
          mid: POINT_STORE_ID,
          billingToken: card.billing_token,
        });

      // 빌링 해지 실패 처리
      if (billingStatus !== 200 || billingData.code !== 'A0200') {
        MessageAlert.warning('구독 취소에 실패하였습니다!');
        return;
      }

      const newCardList = cardList.filter((c) => c.card_id !== card.card_id);
      userDataAction.setData('cardList', newCardList);

      MessageAlert.success('카드정보가 성공적으로 삭제되었습니다.');
    } catch (err) {
      console.error('[handleDeleteCard] Error!! ', err);
      MessageAlert.error('카드 삭제에 실패하였습니다!');
    }
  };

  /**
   * 카드 등록 팝업
   * --
   * @param {string} t
   */
  const handleOpenCardPopup = (t) => {
    openPopup(`/common/card/register?type=${t}`);
  };

  /* ===== HOOKS ===== */
  /**
   * 쇼핑몰 오더 데이터 정제
   * --
   */
  const OrderList = React.useMemo(() => {
    const RegularList = orderList.filter(
      (order) => order.type === 'SUBSCRIPTION',
    );

    const newOrderList = RegularList.map((order) => ({
      ...order,
      parcel_service: order.parcel_service
        .filter((parcel) =>
          selectedTab === 'ACTIVE'
            ? parcel.is_active === 'Y'
            : parcel.is_active === 'N',
        )
        .map((parcel) => ({
          ...parcel,
          review: reviews.find(
            (review) => parcel.parcel_service_id === review.parcel_service_id,
          ),
        })),
    }));

    // parcel_service가 빈배열인 Shop Order 제거
    const SortOrderList = newOrderList
      .filter((order) => order.parcel_service.length)
      .sort((a, b) => b.created_at - a.created_at);

    return SortOrderList;
  }, [orderList, reviews, selectedTab]);

  /**
   * 정기배송 카드 정제
   * --
   */
  const CardList = React.useMemo(() => {
    const subscribeCard = cardList.filter(
      (card) => card.type === 'SUBSCRIPTION',
    );

    const newCardList = subscribeCard.map((card) => ({
      ...card,
      subscribe: OrderList.flatMap((order) =>
        order.parcel_service.filter(
          (parcel) =>
            parcel.card_id === card.card_id && parcel.is_active === 'Y',
        ),
      ),
    }));

    return newCardList;
  }, [cardList, OrderList]);

  /**
   * 팝업 메세지 감지
   * --
   */
  React.useEffect(() => {
    // 메시지 수신 핸들러
    const handleMessage = (event) => {
      const allowedOrigins = [
        `http://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `http://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
        `https://mall-jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
      ];

      // 보안 체크: 같은 origin에서 온 메시지만 허용
      if (!allowedOrigins.includes(event.origin)) {
        console.warn('허용되지 않은 origin:', event.origin);
        return;
      }

      const message = event.data;

      // 팝업에서 보낸 데이터만 처리
      if (message?.source === 'card-register') {
        const parseData = JSON.parse(message?.data);

        userDataAction.addData('cardList', parseData);
      }
    };

    window.addEventListener('message', handleMessage);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line
  }, []);

  /* ===== RENDER ===== */
  return (
    <>
      <MypageSubscribePresenter
        order={OrderList}
        isLoading={isLoading}
        onOrderStatus={handleOrderStatus}
        navigate={navigate}
        tabItem={TAP}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        cards={CardList}
        onDeleteCard={handleDeleteCard}
        onOpenCardPopup={handleOpenCardPopup}
        screenSize={screenSize}
      />
    </>
  );
};

export default MypageSubscribeContainer;
